import axios from 'axios';
import { PRINT_EVENT_KEYS } from '@/services/printing/printJobService';

const PRINT_SETTINGS_CACHE_KEY = 'ewu_restaurant_print_settings';

export const PRINT_BEEP_MODES = Object.freeze({
  ALL: 'all',
  COMMANDS: 'commands',
  ACCOUNTS: 'accounts',
});

export const PRINT_BEEP_MODE_OPTIONS = Object.freeze([
  {
    value: PRINT_BEEP_MODES.ALL,
    label: 'Todo',
    description: 'Pita con cualquier trabajo: comandas, cuentas, tickets y facturas.',
  },
  {
    value: PRINT_BEEP_MODES.COMMANDS,
    label: 'Solo comandas',
    description: 'Comandas de QR, TPV e impresión manual.',
  },
  {
    value: PRINT_BEEP_MODES.ACCOUNTS,
    label: 'Solo cuentas',
    description: 'Cuentas impresas desde el TPV.',
  },
]);

export const PRINT_BEEP_COMMAND_OPTIONS = Object.freeze([
  { value: 'auto', label: 'Automático' },
  { value: 'epson', label: 'Epson (ESC ( A)' },
  { value: 'esc_b', label: 'Compatible ESC/POS (ESC B)' },
  // Avisadores de cocina conectados al puerto del cajón (RJ11): suenan con el pulso de apertura.
  { value: 'drawer', label: 'Avisador en el puerto del cajón' },
  { value: 'none', label: 'No pitar' },
]);

// Caracteres por línea de cada impresora térmica (print-agent LINE_WIDTHS). 42 cabe en todas las de
// 80 mm; 48 solo en las de 203 ppp y se elige a mano tras la prueba de ancho.
export const PRINT_LINE_WIDTH_OPTIONS = Object.freeze([
  { value: 42, label: '42' },
  { value: 48, label: '48' },
]);

export function normalizeLineWidth(value) {
  const width = Number(value);
  return PRINT_LINE_WIDTH_OPTIONS.some((option) => option.value === width) ? width : 42;
}

// Tabla de caracteres ESC/POS (€ y tildes). Mismos códigos que api-rest y print-agent (CODE_PAGES).
// La letra es la de cada línea de la prueba de caracteres del agente.
export const PRINT_CODE_PAGE_OPTIONS = Object.freeze([
  { value: 'auto', letter: 'A', label: 'Automática · tabla 19', description: 'Epson y la mayoría de impresoras compatibles.' },
  { value: 't16_1252', letter: 'B', label: 'Tabla 16 · Windows-1252', description: 'Otras impresoras compatibles.' },
  { value: 't14_pc858', letter: 'C', label: 'Tabla 14 · PC858', description: 'Algunas impresoras de marca blanca.' },
  { value: 't11_1252', letter: 'D', label: 'Tabla 11 · Windows-1252', description: 'Algunas impresoras de marca blanca.' },
  { value: 't2_pc850', letter: 'E', label: 'Tabla 2 · PC850', description: 'Funciona en casi todas, pero sin €: imprime «EUR».' },
]);

// Diseños de la comanda impresa. Mismos códigos que api-rest y print-agent (COMMAND_LAYOUTS);
// un agente que no los conoce imprime el estándar.
export const PRINT_COMMAND_LAYOUT_OPTIONS = Object.freeze([
  { value: 'standard', label: 'Estándar', description: 'El diseño de siempre, con el nombre del restaurante arriba.' },
  {
    value: 'extended',
    label: 'Versión ampliada',
    description: 'El servicio en grande arriba y los platos con su cantidad más grandes.',
  },
]);

// Texto antes del servicio en la cabecera de la versión ampliada (api-rest y print-agent).
// Vacío a propósito = solo el servicio; sin dato = «SERVICIO:».
export const DEFAULT_COMMAND_SERVICE_LABEL = 'SERVICIO:';
export const COMMAND_SERVICE_LABEL_MAX_LENGTH = 16;

export function normalizeCommandServiceLabel(value) {
  if (typeof value !== 'string') return DEFAULT_COMMAND_SERVICE_LABEL;
  return value.replace(/\s+/g, ' ').trim().slice(0, COMMAND_SERVICE_LABEL_MAX_LENGTH);
}

// Versión ampliada desde el agente 2.6.0 (api-rest y print-agent COMMAND_LINE_GAPS): espacio entre
// líneas, separador entre planta y mesa, origen y hora en una línea y pie sin «Para».
export const PRINT_COMMAND_LINE_SPACING_OPTIONS = Object.freeze([
  { value: 'compact', label: 'Reducido' },
  { value: 'normal', label: 'Normal' },
  { value: 'wide', label: 'Amplio' },
  { value: 'extra_wide', label: 'Muy amplio' },
]);
export const DEFAULT_COMMAND_LINE_SPACING = 'normal';
export const DEFAULT_COMMAND_FLOOR_SEPARATOR = '-';
export const COMMAND_FLOOR_SEPARATOR_MAX_LENGTH = 12;

export function normalizeCommandLineSpacing(value) {
  const spacing = String(value || '').trim();
  return PRINT_COMMAND_LINE_SPACING_OPTIONS.some((option) => option.value === spacing)
    ? spacing
    : DEFAULT_COMMAND_LINE_SPACING;
}

// Vacío a propósito = planta y mesa separadas solo por un espacio; sin dato = «-».
export function normalizeCommandFloorSeparator(value) {
  if (typeof value !== 'string') return DEFAULT_COMMAND_FLOOR_SEPARATOR;
  return value.replace(/\s+/g, ' ').trim().slice(0, COMMAND_FLOOR_SEPARATOR_MAX_LENGTH);
}

// Agente 2.6.3: separador entre «COMANDA» y el origen, y letra de los productos.
export const DEFAULT_COMMAND_ORIGIN_SEPARATOR = '-';
export const COMMAND_ORIGIN_SEPARATOR_MAX_LENGTH = 12;
export const PRINT_COMMAND_ITEM_FONT_OPTIONS = Object.freeze([
  { value: 'narrow', label: 'Estrecha', hint: 'Caben más letras por línea.' },
  { value: 'narrow_mixed', label: 'Estrecha, con ñ y tildes de la normal',
    hint: 'La ñ, las tildes, ¿, ¡ y € con la letra de la comanda estándar (algo más pequeñas).' },
  { value: 'normal', label: 'Normal', hint: 'Todo el producto con la letra normal: caben menos letras por línea.' },
]);

export function normalizeCommandOriginSeparator(value) {
  if (typeof value !== 'string') return DEFAULT_COMMAND_ORIGIN_SEPARATOR;
  return value.replace(/\s+/g, ' ').trim().slice(0, COMMAND_ORIGIN_SEPARATOR_MAX_LENGTH);
}

export function normalizeCommandItemFont(value) {
  const font = String(value || '').trim();
  return PRINT_COMMAND_ITEM_FONT_OPTIONS.some((option) => option.value === font) ? font : 'narrow';
}

export function normalizePrintCommandLayout(value) {
  const layout = String(value || '').trim();
  return PRINT_COMMAND_LAYOUT_OPTIONS.some((option) => option.value === layout) ? layout : 'standard';
}

export function normalizePrintCodePage(value) {
  const code = String(value || '').trim();
  return PRINT_CODE_PAGE_OPTIONS.some((option) => option.value === code) ? code : 'auto';
}

export function normalizeBeepCommand(value) {
  const command = String(value || '').trim().toLowerCase();
  return PRINT_BEEP_COMMAND_OPTIONS.some((option) => option.value === command) ? command : 'auto';
}

// Misma regla que api-rest/ewu/apps/app/print_jobs.py; en cloud decide el servidor.
const PRINT_BEEP_EVENT_KEYS = Object.freeze({
  [PRINT_BEEP_MODES.COMMANDS]: [
    PRINT_EVENT_KEYS.QR_ORDER_CREATED,
    PRINT_EVENT_KEYS.POS_ORDER_CREATED,
    PRINT_EVENT_KEYS.MANUAL_KITCHEN_PRINT,
  ],
  [PRINT_BEEP_MODES.ACCOUNTS]: [PRINT_EVENT_KEYS.ACCOUNT_PRINT],
});

export function normalizePrintSettings(settings = {}) {
  const mode = String(settings?.print_beep_mode || '').trim();
  return {
    restaurant_id: String(settings?.restaurant_id || '').trim(),
    print_beep_enabled: settings?.print_beep_enabled === true,
    print_beep_mode: Object.values(PRINT_BEEP_MODES).includes(mode) ? mode : PRINT_BEEP_MODES.ALL,
    print_code_page: normalizePrintCodePage(settings?.print_code_page),
    print_command_layout: normalizePrintCommandLayout(settings?.print_command_layout),
    print_command_service_label: normalizeCommandServiceLabel(settings?.print_command_service_label),
    print_command_line_spacing: normalizeCommandLineSpacing(settings?.print_command_line_spacing),
    print_command_floor_separator: normalizeCommandFloorSeparator(settings?.print_command_floor_separator),
    print_command_meta_inline: settings?.print_command_meta_inline === true,
    print_command_footer_hide_para: settings?.print_command_footer_hide_para === true,
    print_command_hide_product_header: settings?.print_command_hide_product_header === true,
    print_command_origin_in_title: settings?.print_command_origin_in_title === true,
    print_command_origin_separator: normalizeCommandOriginSeparator(settings?.print_command_origin_separator),
    print_command_item_font: normalizeCommandItemFont(settings?.print_command_item_font),
  };
}

export function printBeepApplies(settings = {}, eventKey = '') {
  const normalized = normalizePrintSettings(settings);
  if (!normalized.print_beep_enabled) return false;
  if (normalized.print_beep_mode === PRINT_BEEP_MODES.ALL) return true;
  return (PRINT_BEEP_EVENT_KEYS[normalized.print_beep_mode] || []).includes(String(eventKey || '').trim());
}

function cachePrintSettings(settings) {
  if (!settings.restaurant_id || typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(`${PRINT_SETTINGS_CACHE_KEY}:${settings.restaurant_id}`, JSON.stringify(settings));
  } catch (error) {
    console.warn('No se pudo guardar la caché de ajustes de impresión:', error);
  }
}

export function getCachedPrintSettings(restaurantId) {
  const key = String(restaurantId || '').trim();
  if (!key || typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const stored = JSON.parse(window.localStorage.getItem(`${PRINT_SETTINGS_CACHE_KEY}:${key}`) || 'null');
    return stored && stored.restaurant_id === key ? normalizePrintSettings(stored) : null;
  } catch (error) {
    return null;
  }
}

// Impresión directa al agente (modo local o cola offline): sin servidor que
// decida, se usa el último ajuste conocido del restaurante. Sin caché no pita.
export function cachedPrintBeepFor(restaurantId, eventKey) {
  const settings = getCachedPrintSettings(restaurantId);
  return Boolean(settings) && printBeepApplies(settings, eventKey);
}

export function cachedPrintCodePageFor(restaurantId) {
  return getCachedPrintSettings(restaurantId)?.print_code_page || 'auto';
}

export function cachedPrintCommandLayoutFor(restaurantId) {
  return getCachedPrintSettings(restaurantId)?.print_command_layout || 'standard';
}

export function cachedPrintCommandServiceLabelFor(restaurantId) {
  const settings = getCachedPrintSettings(restaurantId);
  return settings ? settings.print_command_service_label : DEFAULT_COMMAND_SERVICE_LABEL;
}

// Ajustes de la versión ampliada para la impresión directa; sin caché, los de por defecto.
export function cachedPrintCommandDesignFor(restaurantId) {
  const settings = getCachedPrintSettings(restaurantId) || normalizePrintSettings();
  return {
    print_command_line_spacing: settings.print_command_line_spacing,
    print_command_floor_separator: settings.print_command_floor_separator,
    print_command_meta_inline: settings.print_command_meta_inline,
    print_command_footer_hide_para: settings.print_command_footer_hide_para,
    print_command_hide_product_header: settings.print_command_hide_product_header,
    print_command_origin_in_title: settings.print_command_origin_in_title,
    print_command_origin_separator: settings.print_command_origin_separator,
    print_command_item_font: settings.print_command_item_font,
  };
}

export async function getRestaurantPrintSettings(apiBaseUrl, token) {
  const response = await axios.get(`${apiBaseUrl}/v1/api/app/print-settings`, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 10000,
  });
  const settings = normalizePrintSettings(response.data);
  cachePrintSettings(settings);
  return settings;
}

export async function saveRestaurantPrintSettings(apiBaseUrl, token, payload) {
  const response = await axios.put(`${apiBaseUrl}/v1/api/app/print-settings`, payload, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 10000,
  });
  const settings = normalizePrintSettings(response.data);
  cachePrintSettings(settings);
  return settings;
}
